import React from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import * as R from 'remeda'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import {
  findByPath,
  formatNineSegment,
  pathOr,
  reversedNineSegments,
  toNineNumber,
} from '~/utils'

import { useBrandGroup } from '../hooks/useBrandGroup'
import { useSurveyId } from '../hooks/useSurveyId'
import { type InputValues, resolver } from '../utils'

import { ConditionGroupsInput } from './ConditionGroupsInput'
import styles from './SegmentDefinitionsInput.css'

export type SegmentDefinitionsInputProps = {
  onSave: (segment_definitions: InputValues['segment_definitions']) => void
}

export const SegmentDefinitionsInput = ({
  onSave,
}: SegmentDefinitionsInputProps) => {
  const { t } = useTranslation()
  const brandGroup = useBrandGroup()
  const methods = useForm<InputValues>({
    defaultValues: {
      segment_definitions: reversedNineSegments.map((nineSegment) => ({
        segment_number: toNineNumber(nineSegment),
        condition_groups: R.pipe(
          brandGroup?.segment_definitions ?? [],
          findByPath('segment_number', toNineNumber(nineSegment)),
          pathOr('condition_groups', [[]]),
        ),
      })),
    },
    resolver,
  })
  const { handleSubmit } = methods

  const surveyId = useSurveyId()

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ segment_definitions }) => {
      onSave(segment_definitions)
    },
    [onSave],
  )

  return (
    <FormProvider {...methods}>
      <Box className={styles.root} verticalAppearance="stretch-scroll">
        <>
          <Typography
            className={styles.header}
            textAlign="center"
            variant="heading2"
            whiteSpace="preWrap"
          >
            {t('segment_number_label')}
          </Typography>

          <Typography
            className={styles.header}
            textAlign="center"
            variant="heading2"
          >
            {t('segment_name')}
          </Typography>

          <Typography
            className={styles.header}
            textAlign="center"
            variant="heading2"
          >
            {t('segment_logic')}
          </Typography>
        </>

        {reversedNineSegments.map((nineSegment, nineSegmentIndex) => (
          <React.Fragment key={nineSegment}>
            <Typography fontSize={16} textAlign="center" variant="normal">
              {formatNineSegment(nineSegment, '%n', t)}
            </Typography>

            <Typography
              fontSize={16}
              textAlign="center"
              variant="normal"
              whiteSpace="preWrap"
            >
              {formatNineSegment(nineSegment, '%l', t)}
            </Typography>

            <ConditionGroupsInput nineSegmentIndex={nineSegmentIndex} />
          </React.Fragment>
        ))}

        <HStack className={styles.buttons} justify="space-between" spacing={4}>
          <Link to={compile('brandGroupList', { surveyId })}>
            <StyledButton>{t('back')}</StyledButton>
          </Link>

          <StyledButton onClick={handleSubmit(handleValid)}>
            {t('save')}
          </StyledButton>
        </HStack>
      </Box>
    </FormProvider>
  )
}
